import { Box, styled } from '@mui/material';
import { useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  ControlButton,
  Controls,
  Edge,
  Node,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { WorkflowNode } from 'src/pages/Settings/workflows/components/workflow-node';
import {
  WorkflowLayoutDirection,
  WorkflowNodeDisplayData,
} from 'src/pages/Settings/workflows/utils/workflow-helpers';

const nodeTypes = { workflowNode: WorkflowNode };

const DiagramContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  minHeight: 0,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

const DiagramInner = ({
  nodes: initialNodes,
  edges,
  onNodeClick,
  onNodesChange,
  layoutDirection = WorkflowLayoutDirection.Down,
  onLayoutChange,
}: {
  nodes: Node<WorkflowNodeDisplayData>[];
  edges: Edge[];
  onNodeClick?: Parameters<typeof ReactFlow>[0]['onNodeClick'];
  onNodesChange?: Parameters<typeof ReactFlow>[0]['onNodesChange'];
  layoutDirection?: WorkflowLayoutDirection;
  onLayoutChange?: (direction: WorkflowLayoutDirection) => void;
}) => {
  const [isInteractive, setIsInteractive] = useState(true);

  // Update nodes' draggable property based on interactive state
  const nodes = useMemo(
    () =>
      initialNodes.map((node) => ({
        ...node,
        draggable: isInteractive,
      })),
    [initialNodes, isInteractive],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      nodesDraggable={isInteractive}
      // for MVP it's just read-only diagram
      nodesConnectable={false}
      elementsSelectable={isInteractive}
      panOnScroll
      panOnDrag
      zoomOnScroll
      zoomOnPinch
      fitView
      minZoom={0.1}
      maxZoom={4}
      proOptions={{ hideAttribution: true }}
      onNodeClick={onNodeClick}
      onNodesChange={onNodesChange}
    >
      <Background />
      <Controls onInteractiveChange={setIsInteractive}>
        <ControlButton
          title="Vertical layout"
          aria-pressed={layoutDirection === WorkflowLayoutDirection.Down}
          onClick={() => onLayoutChange?.(WorkflowLayoutDirection.Down)}
        >
          <DynamicIcon name="flex_direction" fontSize="small" sx={{ color: 'black' }} />
        </ControlButton>
        <ControlButton
          title="Horizontal layout"
          aria-pressed={layoutDirection === WorkflowLayoutDirection.Right}
          onClick={() => onLayoutChange?.(WorkflowLayoutDirection.Right)}
        >
          <DynamicIcon name="flex_no_wrap" fontSize="small" sx={{ color: 'black' }} />
        </ControlButton>
      </Controls>
    </ReactFlow>
  );
};

DiagramInner.displayName = 'WorkflowDiagramInner';

export const WorkflowDiagram = ({
  nodes,
  edges,
  height = 560,
  onNodeClick,
  onNodesChange,
  layoutDirection,
  onLayoutChange,
}: {
  nodes: Node<WorkflowNodeDisplayData>[];
  edges: Edge[];
  height?: number | string;
  onNodeClick?: Parameters<typeof ReactFlow>[0]['onNodeClick'];
  onNodesChange?: Parameters<typeof ReactFlow>[0]['onNodesChange'];
  layoutDirection?: WorkflowLayoutDirection;
  onLayoutChange?: (direction: WorkflowLayoutDirection) => void;
}) => (
  <DiagramContainer height={height}>
    <ReactFlowProvider>
      <DiagramInner
        nodes={nodes}
        edges={edges}
        onNodeClick={onNodeClick}
        onNodesChange={onNodesChange}
        layoutDirection={layoutDirection}
        onLayoutChange={onLayoutChange}
      />
    </ReactFlowProvider>
  </DiagramContainer>
);
